import { Route, Routes } from 'react-router';
import { Trans } from '@lingui/react/macro';

import { EmptyState, IllustrationNoResults } from '@/components/EmptyState';
import { TableQueryWrapper } from '@/components/TableQueryWrapper/TableQueryWrapper';
import { PaginationFooter } from '@/design_system/Pagination/Pagination';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconPicture from '@/icons/Picture.svg';
import { ProductFromGetMany, useProducts } from '@/models/product';
import { ProductAttributeTrees } from '@/routes/SettingsBeta/components/ProductAttributeTrees/ProductAttributeTrees';
import { useDatabaseTranslation } from '@/services/i18n';
import { createBEMClasses } from '@/utils/classname';
import { formatListWithMore } from '@/utils/list';
import { formatCurrency } from '@/utils/number';

import { Filters } from './Filters/Filters';
import { FiltersStateContext, useFiltersState } from './Filters/FiltersState';
import { Product as ProductPage } from './Product/Product';

import './Products.css';

const { block, element } = createBEMClasses('settings__data-catalogs__products');

const PRODUCTS_PER_PAGE = 20;

export const ProductsRoutes = () => {
  return (
    <Routes>
      <Route path="" element={<Products />} />
      <Route path="/:id/*" element={<ProductPage />} />
    </Routes>
  );
};

const Products = () => {
  const filters = useFiltersState();

  const {
    data: { products, meta } = { products: [] },
    isLoading: isProductsLoading,
    isPlaceholderData: isProductsPlaceholder,
    isError: isProductsError,
  } = useProducts(
    {
      limit: PRODUCTS_PER_PAGE,
      offset: (filters.page.page - 1) * PRODUCTS_PER_PAGE,
      query: filters.search.debouncedSearch || undefined,
      brands: filters.brands.brands.map(({ id }) => id),
    },
    {
      keepPreviousData: true,
    }
  );

  return (
    <main style={{ gap: '1rem' }} className={block()}>
      <FiltersStateContext value={filters}>
        <Filters />
      </FiltersStateContext>
      <TableQueryWrapper
        isLoading={isProductsLoading || isProductsPlaceholder}
        isError={isProductsError}
      >
        {products && products.length === 0 ? (
          <ProductsEmptyState />
        ) : (
          <Table columnWidths={['1fr', '3fr', '2fr', '1fr']} bordered>
            <ProductsHeader />

            <ProductsBody products={products} />

            <PaginationFooter
              itemsPerPage={PRODUCTS_PER_PAGE}
              page={filters.page.page}
              onPageChange={filters.page.setPage}
              count={meta?.count}
            />
          </Table>
        )}
      </TableQueryWrapper>
    </main>
  );
};

const ProductsHeader = () => {
  return (
    <Header>
      <Row>
        <Column>
          <Trans id="settings.organization.data-catalogs.products.table.header.external_id">
            Product ID
          </Trans>
        </Column>
        <Column>
          <Trans id="settings.organization.data-catalogs.products.table.header.name">Name</Trans>
        </Column>
        <Column>
          <Trans id="settings.organization.data-catalogs.products.table.header.product-attributes">
            Product Attributes
          </Trans>
        </Column>
        <Column>
          <Trans id="settings.organization.data-catalogs.products.table.header.product-data">
            Product data
          </Trans>
        </Column>
      </Row>
    </Header>
  );
};

const ProductsBody = ({ products }: { products: ProductFromGetMany[] }) => {
  return (
    <Body>
      {products.map((product) => (
        <ProductRow key={product.id} product={product} />
      ))}
    </Body>
  );
};

const ProductsEmptyState = () => {
  return (
    <EmptyState
      subtitle={
        <Stack>
          <Paragraph>
            <Trans id="settings.organization.data-catalogs.products.no-results.1">
              No results are matching your criteria.
            </Trans>
          </Paragraph>
          <Paragraph>
            <Trans id="settings.organization.data-catalogs.products.no-results.2">
              Please try with different keywords or filters.
            </Trans>
          </Paragraph>
        </Stack>
      }
      illustration={<IllustrationNoResults />}
    />
  );
};

const ProductRow = ({ product }: { product: ProductFromGetMany }) => {
  const { _db } = useDatabaseTranslation();

  const brandLabel = product.brand?.name ?? product.otherBrandName ?? '-';
  const productColors = product.colorsWithImage?.map((colorWithImage) => colorWithImage.color);

  return (
    <Row to={`/settings_beta/catalog/products/${product.id}`}>
      <Cell isLink>
        <Stack gap="0.25rem">
          <Paragraph weight="medium">{product.externalId}</Paragraph>
          <Paragraph size="200">{brandLabel}</Paragraph>
        </Stack>
      </Cell>

      <Cell>
        <Stack row gap="1rem" alignItems="center" flexWrap="nowrap">
          {product.data.image ? (
            <img
              alt={product.name}
              src={product.data.image}
              className={element('product_row__img')}
            />
          ) : (
            <div className={element('product_row__img', { placeholder: true })}>
              <IconPicture className="text-disabled" />
            </div>
          )}
          <Paragraph>{product.name}</Paragraph>
        </Stack>
      </Cell>

      <Cell>
        <ProductAttributeTrees productAttributeValues={product.productAttributeValues} />
      </Cell>

      <Cell>
        <Stack gap="0.25rem">
          {productColors?.length ? (
            <Paragraph size="200">
              <Trans id="settings.organization.data-catalogs.products.table.product-data.colors">
                Colors:
              </Trans>{' '}
              <InlineText size="200" weight="medium">
                {formatListWithMore(
                  productColors.map((color) => _db(color)),
                  5
                )}
              </InlineText>
            </Paragraph>
          ) : null}

          {product.sizes ? (
            <Paragraph size="200">
              <Trans id="settings.organization.data-catalogs.products.table.product-data.sizes">
                Sizes:
              </Trans>{' '}
              <InlineText size="200" weight="medium">
                {formatListWithMore(
                  product.sizes.map((size) => _db(size)),
                  5
                )}
              </InlineText>
            </Paragraph>
          ) : null}

          {product.data.collection ? (
            <Paragraph size="200">
              <Trans id="settings.organization.data-catalogs.products.table.product-data.collection">
                Collection:
              </Trans>{' '}
              <InlineText size="200" weight="medium">
                {product.data.collection}
              </InlineText>
            </Paragraph>
          ) : null}

          {product.data.purchaseValue ? (
            <Paragraph size="200">
              <Trans id="settings.organization.data-catalogs.products.table.product-data.retail-price">
                Retail price:
              </Trans>{' '}
              <InlineText size="200" weight="medium">
                {formatCurrency(
                  product.data.purchaseValue.amount,
                  product.data.purchaseValue.currency
                )}
              </InlineText>
            </Paragraph>
          ) : null}
        </Stack>
      </Cell>
    </Row>
  );
};
